/**
 * Cotações de fornecedores (C3b; docs/adr/0019-cotacoes-de-fornecedores.md), usadas pela API e
 * pelas telas: situações, entradas, totais, regra de mínimo de propostas e o que impede a escolha.
 * A escolha de uma proposta não aprova a compra: a aprovação da contratação é a entrega A1.
 */
import { z } from 'zod';
import { fromScaled, toScaled } from './money.ts';
import { currencyCode, decimalString, uuid } from './schemas.ts';
import type { ContractingBlocker } from './partners.ts';
import type { PurchaseStatus } from './purchasing.ts';

export const QUOTATION_STATUSES = ['open', 'decided', 'cancelled'] as const;
export type QuotationStatus = (typeof QUOTATION_STATUSES)[number];

export const QUOTATION_SUPPLIER_STATUSES = ['invited', 'declined', 'removed'] as const;
export type QuotationSupplierStatus = (typeof QUOTATION_SUPPLIER_STATUSES)[number];

export const PROPOSAL_STATUSES = ['current', 'superseded', 'withdrawn'] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];

/** Número que as pessoas veem: COT-0001. */
export function quotationNumber(n: number): string {
  return `COT-${String(n).padStart(4, '0')}`;
}

/** O que quem compra pode fazer com a cotação agora. As telas mostram só esses botões. */
export function quotationActions(
  requestStatus: PurchaseStatus,
  quotationStatus: QuotationStatus | null,
  buyer: boolean,
) {
  const quoting = buyer && requestStatus === 'quoting';
  const open = quoting && quotationStatus === 'open';
  return {
    open: quoting && (quotationStatus === null || quotationStatus === 'cancelled'),
    inviteSupplier: open,
    recordProposal: open,
    select: open,
    reopen: quoting && quotationStatus === 'decided',
    cancel: quoting && (quotationStatus === 'open' || quotationStatus === 'decided'),
    /** Enviar a proposta escolhida para a aprovação da contratação (ADR 0021). */
    submit: quoting && quotationStatus === 'decided',
  };
}
export type QuotationActions = ReturnType<typeof quotationActions>;

// ----- Contas sem ponto flutuante -----

const DECIMAL_RE = /^(\d+)(?:\.(\d+))?$/;

function parseDecimal(value: string): { int: bigint; scale: number } {
  const m = DECIMAL_RE.exec(value.trim());
  if (!m) throw new Error(`Valor inválido: "${value}"`);
  const frac = m[2] ?? '';
  return { int: BigInt(m[1]! + frac), scale: frac.length };
}

/** a × b, arredondado (metade para cima) para as 4 casas do dinheiro. Só valores positivos. */
export function multiplyAmount(a: string, b: string): string {
  const x = parseDecimal(a);
  const y = parseDecimal(b);
  const scale = x.scale + y.scale;
  const product = x.int * y.int;
  if (scale <= 4) return fromScaled(product * 10n ** BigInt(4 - scale));
  const divisor = 10n ** BigInt(scale - 4);
  return fromScaled((product + divisor / 2n) / divisor);
}

/**
 * Converte um valor pela taxa de câmbio (valor × taxa; com `inverse`, valor ÷ taxa), arredondado
 * para 4 casas. A taxa vem como texto, direto da tabela de câmbio.
 */
export function convertAmount(amount: string, rate: string, inverse = false): string {
  if (!inverse) return multiplyAmount(amount, rate);
  const r = parseDecimal(rate);
  if (r.int === 0n) throw new Error('Taxa de câmbio zerada');
  const numerator = toScaled(amount) * 10n ** BigInt(r.scale);
  return fromScaled((numerator * 2n + r.int) / (2n * r.int));
}

export interface ProposalLine {
  requestItemId: string;
  quantity: string;
  unitPrice: string;
}

/** Total da proposta: soma de quantidade × preço de cada item, mais o frete. */
export function proposalTotal(lines: readonly ProposalLine[], freightAmount: string): string {
  return fromScaled(
    lines.reduce(
      (sum, l) => sum + toScaled(multiplyAmount(l.quantity, l.unitPrice)),
      toScaled(freightAmount),
    ),
  );
}

/** Periodicidade de um serviço recorrente (a mesma dos critérios de recebimento por período). */
export const RECURRENCE_FREQUENCIES = ['weekly', 'monthly', 'quarterly', 'yearly'] as const;
export type RecurrenceFrequency = (typeof RECURRENCE_FREQUENCIES)[number];

/** Meses que contam na alçada de um contrato recorrente (ADR 0080: o valor anual). */
export const APPROVAL_MONTHS = 12;

/** Serviço recorrente da compra: periodicidade e duração do contrato, em meses. */
export interface RecurringItem {
  frequency: RecurrenceFrequency;
  months: number;
}

/** A conta do valor que vai para a aprovação da contratação, guardada com a escolha. */
export interface ApprovalBasis {
  /** Total da proposta escolhida (itens e frete), como sempre. */
  contractTotal: string;
  /** Valor enviado à alçada: os recorrentes contam 12 meses (ou o prazo, se menor). */
  approvalAmount: string;
  currency: string;
  recurring: {
    requestItemId: string;
    frequency: RecurrenceFrequency;
    months: number;
    lineTotal: string;
    countedTotal: string;
  }[];
}

/**
 * Valor da aprovação da contratação (ADR 0080, adendo de Compras): cada serviço recorrente conta
 * 12 meses do contrato (o total × 12 ÷ duração), ou o total quando a duração é de até 12 meses.
 * Os demais itens e o frete contam inteiros. Sem recorrentes, é o total da proposta.
 */
export function approvalBasis(
  lines: readonly ProposalLine[],
  freightAmount: string,
  currency: string,
  recurringByItem: ReadonlyMap<string, RecurringItem>,
): ApprovalBasis {
  let counted = toScaled(freightAmount);
  const recurring: ApprovalBasis['recurring'] = [];
  for (const l of lines) {
    const lineTotal = toScaled(multiplyAmount(l.quantity, l.unitPrice));
    const r = recurringByItem.get(l.requestItemId);
    if (!r) {
      counted += lineTotal;
      continue;
    }
    const months = BigInt(r.months);
    // total × 12 ÷ meses, arredondado (metade para cima) nas 4 casas do dinheiro.
    const part =
      r.months > APPROVAL_MONTHS
        ? (lineTotal * BigInt(APPROVAL_MONTHS) * 2n + months) / (2n * months)
        : lineTotal;
    counted += part;
    recurring.push({
      requestItemId: l.requestItemId,
      frequency: r.frequency,
      months: r.months,
      lineTotal: fromScaled(lineTotal),
      countedTotal: fromScaled(part),
    });
  }
  return {
    contractTotal: proposalTotal(lines, freightAmount),
    approvalAmount: fromScaled(counted),
    currency,
    recurring,
  };
}

/** A proposta ainda vale na data (sem validade informada, vale). */
export function proposalIsValid(
  p: { status: ProposalStatus; validUntil: string | null },
  today: string,
): boolean {
  return p.status === 'current' && (p.validUntil == null || p.validUntil >= today);
}

// ----- Quantidade mínima de propostas -----

export interface QuoteRuleLike {
  id: string;
  legalEntityId: string | null;
  minAmount: string | null;
  currency: string | null;
  minProposals: number;
  isActive: boolean;
}

/**
 * Quantas propostas válidas a escolha exige. Valem as regras ativas da empresa; sem nenhuma, as
 * do grupo. Das regras cuja faixa de valor alcança o total, vale a maior exigência. Sem cotação
 * de câmbio para comparar o valor, a regra vale (na dúvida, exige mais). Sem regra: 1.
 */
export function requiredProposals(
  rules: readonly QuoteRuleLike[],
  legalEntityId: string,
  amountIn: (currency: string) => string | null,
): { minProposals: number; ruleId: string | null } {
  const active = rules.filter((r) => r.isActive);
  const own = active.filter((r) => r.legalEntityId === legalEntityId);
  const candidates = own.length > 0 ? own : active.filter((r) => r.legalEntityId == null);
  let best: { minProposals: number; ruleId: string | null } = { minProposals: 1, ruleId: null };
  for (const r of candidates) {
    let applies = true;
    if (r.minAmount != null && r.currency != null) {
      const amount = amountIn(r.currency);
      applies = amount == null || toScaled(amount) >= toScaled(r.minAmount);
    }
    if (applies && (best.ruleId === null || r.minProposals > best.minProposals)) {
      best = { minProposals: r.minProposals, ruleId: r.id };
    }
  }
  return best;
}

// ----- O que impede escolher uma proposta -----

export type SelectionBlocker =
  | 'not_current'
  | 'expired'
  | 'incomplete'
  | 'supplier_not_participating'
  | 'supplier_not_apt'
  | 'exception_required';

export function selectionBlockers(input: {
  proposal: { status: ProposalStatus; validUntil: string | null };
  supplierStatus: QuotationSupplierStatus;
  /** Itens ativos da compra que a proposta não cotou. */
  missingItems: number;
  contracting: { apt: boolean; blockers: readonly ContractingBlocker[] };
  validProposals: number;
  minProposals: number;
  hasExceptionJustification: boolean;
  today: string;
}): SelectionBlocker[] {
  const blockers: SelectionBlocker[] = [];
  if (input.proposal.status !== 'current') blockers.push('not_current');
  else if (!proposalIsValid(input.proposal, input.today)) blockers.push('expired');
  if (input.missingItems > 0) blockers.push('incomplete');
  if (input.supplierStatus !== 'invited') blockers.push('supplier_not_participating');
  if (!input.contracting.apt) blockers.push('supplier_not_apt');
  if (input.validProposals < input.minProposals && !input.hasExceptionJustification) {
    blockers.push('exception_required');
  }
  return blockers;
}

// ----- Entradas -----

const text = (max: number) => z.string().trim().min(1).max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

export const quotationOpenInput = z.object({
  responseDue: z.iso.date().nullable().optional(),
  instructions: optionalText(4000),
});

export const quotationSupplierInput = z.object({
  partnerId: uuid,
  contactNotes: optionalText(500),
});

export const quotationSupplierStatusInput = z.object({
  status: z.enum(['declined', 'removed']),
  reason: text(1000),
});

export const proposalItemInput = z.object({
  requestItemId: uuid,
  quantity: decimalString(10, 4).refine(
    (v) => Number(v) > 0,
    'A quantidade precisa ser maior que zero',
  ),
  unitPrice: decimalString(15, 4),
  notes: optionalText(1000),
});

export const proposalInput = z.object({
  quotationSupplierId: uuid,
  currency: currencyCode,
  freightAmount: decimalString(15, 4).default('0'),
  deliveryDays: z.number().int().min(0).max(3650).nullable().optional(),
  paymentTerms: optionalText(500),
  validUntil: z.iso.date().nullable().optional(),
  receivedOn: z.iso.date(),
  notes: optionalText(4000),
  items: z
    .array(proposalItemInput)
    .min(1, 'Informe o preço de pelo menos um item')
    .max(200)
    .refine(
      (items) => new Set(items.map((i) => i.requestItemId)).size === items.length,
      'Cada item aparece uma vez só',
    ),
});
export type ProposalInput = z.infer<typeof proposalInput>;

export const proposalWithdrawInput = z.object({ reason: text(1000) });

export const proposalSelectionInput = z.object({
  proposalId: uuid,
  /** Obrigatória, menos na compra dentro de contrato vigente, que grava a do contrato (ADR 0102). */
  justification: z
    .string()
    .trim()
    .max(4000)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional()
    .refine((v) => v == null || v.length >= 10, 'Explique a escolha (pelo menos 10 letras)'),
  exceptionJustification: z
    .string()
    .trim()
    .max(4000)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional()
    .refine((v) => v == null || v.length >= 10, 'Explique a exceção (pelo menos 10 letras)'),
});

export const quotationReasonInput = z.object({ reason: text(1000) });

export const quoteRuleInput = z
  .object({
    legalEntityId: uuid.nullable(),
    minAmount: decimalString(15, 4).nullable().optional(),
    currency: currencyCode.nullable().optional(),
    minProposals: z.number().int().min(1).max(10),
    notes: optionalText(500),
    isActive: z.boolean().default(true),
  })
  .refine((v) => (v.minAmount == null) === (v.currency == null), {
    message: 'Informe o valor e a moeda juntos',
    path: ['currency'],
  });
export type QuoteRuleInput = z.infer<typeof quoteRuleInput>;
